import { Space, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import { memo } from 'react';
import type { PriceRule } from '@/services/drama/api';
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';

const { Text } = Typography;

const DIM_LABEL: Record<string, string> = {
  resolution: '分辨率',
  duration: '时长',
  size: '比例',
};

export interface CreditCellProps {
  creditPrice?: number;
  priceRules?: PriceRule[];
}

const useStyles = createStyles(({ token, css }) => ({
  tipList: css`
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 12px;
  `,
  tipItem: css`
    display: flex;
    gap: 6px;
    line-height: 1.6;
  `,
  tipIndex: css`
    color: ${token.colorTextTertiary};
  `,
  tipFallback: css`
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed ${token.colorBorderSecondary};
    color: ${token.colorTextSecondary};
  `,
}));

function describeMatch(match: Record<string, string>): string {
  const parts = Object.entries(match)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${DIM_LABEL[k] || k}=${v}`);
  if (parts.length === 0) return '通配(任意维度)';
  return parts.join(' 且 ');
}

function CreditCellInner({ creditPrice, priceRules }: CreditCellProps) {
  const { styles } = useStyles();
  const fallback = creditPrice ?? 0;
  const rules = priceRules?.filter((r) => r && r.price >= 0) ?? [];

  if (rules.length === 0) {
    if (fallback === 0) return <Tag>未配置</Tag>;
    return <Text strong>{fallback}c</Text>;
  }

  const prices = rules.map((r) => r.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = min === max ? `${min}c` : `${min}~${max}c`;

  const tip = (
    <div>
      <ul className={styles.tipList}>
        {rules.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: 规则数组下标即优先级,无稳定 id
          <li key={i} className={styles.tipItem}>
            <span className={styles.tipIndex}>#{i + 1}</span>
            <span>
              {describeMatch(r.match || {})} → <b>{r.price}c</b>
            </span>
          </li>
        ))}
      </ul>
      <div className={styles.tipFallback}>
        {fallback > 0
          ? `不匹配兜底:${fallback}c / 次`
          : '不匹配无兜底(legacy 兼容,后端不扣费 + 告警)'}
      </div>
    </div>
  );

  return (
    <Tooltip
      title={tip}
      styles={{ root: { maxWidth: 360 } }}
      mouseEnterDelay={0.1}
    >
      <Space size={4}>
        <Tag style={{ margin: 0 }}>{rules.length} 档</Tag>
        <Text strong>{range}</Text>
      </Space>
    </Tooltip>
  );
}

const CreditCell = memo(CreditCellInner);
export default CreditCell;
